import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Form, Input, Select } from 'antd';

import { useChangeConsultantRefMutation } from 'app/services/edpApiV1/references';
import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';
import { useAppDispatch, useAppSelector } from 'app/store';

import { uiModel } from 'entities/ui';
import { viewerModel } from 'entities/viewer';

import { getErrorData } from 'shared/helpers/utils';
import { usePath } from 'shared/hooks';
import { useNotifications } from 'shared/hooks/use-notifications';
import { TPreparedReferencesData } from 'shared/types/reference.types';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { RolesUser } from 'shared/enums';
import style from './index.module.scss';

const ReferencesUserForm = ({
  dataReferences,
  getReferenceTrigger,
}: {
  dataReferences: TPreparedReferencesData[];
  getReferenceTrigger?: (data: { requestId: number; allAvailableOrganizations: boolean }) => void;
}) => {
  const [btnVisible, setBtnVisible] = useState<boolean>(false);
  const [selectDisabled, setSelectDisabled] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { id } = useParams();
  const path = usePath(2);
  const referencesHrId = dataReferences[0]?.hr?.id;
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const { role, id: profileId } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const { data: allSubordinates } = useSubordinatesQuery({ include: 'me' });

  const [changeConsultantRef, result] = useChangeConsultantRefMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });

  const {
    isSuccess: successChangeConsultantRef,
    isError: isErrorChangeConsultantRef,
    // error: errorChangeConsultantRef,
    reset: resetChangeConsultantRef,
  } = result;

  const selectHandler = (selectedHrId: number) => {
    if (selectedHrId === referencesHrId) {
      setBtnVisible(false);
    } else {
      setBtnVisible(true);
    }
  };

  const onFinish = (values: { consultantId: number }) => {
    const { consultantId } = values;
    const bodyRequest = {
      requestId: Number(id),
      hrId: consultantId,
      allAvailableOrganizations,
    };
    if (consultantId !== profileId) {
      dispatch(uiModel.Common.actions.blockHrActions(true));
    } else {
      dispatch(uiModel.Common.actions.blockHrActions(false));
    }
    changeConsultantRef(bodyRequest);
    setBtnVisible(false);
  };

  useEffect(() => {
    if (getReferenceTrigger) {
      getReferenceTrigger({ requestId: Number(id), allAvailableOrganizations });
      resetChangeConsultantRef();
    }
  }, [successChangeConsultantRef]);

  useEffect(() => {
    if (path === 'myArchive' && role !== RolesUser.HRDirector && role !== RolesUser.SecondLine) {
      setSelectDisabled(true);
    }
  }, [role, path]);

  useEffect(() => {
    if (role !== RolesUser.HRDirector && role !== RolesUser.SecondLine) {
      setSelectDisabled(true);
    }
  }, [role]);

  useEffect(() => {
    if (dataReferences[0]?.status?.text === 'Готова к выдаче' || dataReferences[0]?.status?.text === 'Отклонена') {
      setSelectDisabled(true);
    }
  }, [dataReferences]);

  useEffect(() => {
    if (dataReferences && dataReferences?.length > 0) {
      form.setFields([
        {
          name: 'tabel',
          value: dataReferences[0]?.personnelNumber,
        },
        {
          name: 'exemplarsAmount',
          value: dataReferences[0]?.amount,
        },
        {
          name: 'division',
          value: dataReferences[0]?.division,
        },
        {
          name: 'position',
          value: dataReferences[0]?.position,
        },
        {
          name: 'consultantId',
          value: dataReferences[0]?.hr?.id,
        },
      ]);
    }
  }, [dataReferences, form]);

  useEffect(() => {
    if (profileId !== referencesHrId) {
      dispatch(uiModel.Common.actions.blockHrActions(true));
    }
    return () => {
      dispatch(uiModel.Common.actions.blockHrActions(false));
    };
  }, []);

  useNotifications(
    successChangeConsultantRef,
    isErrorChangeConsultantRef,
    'Ответственный назначен',
    'Нельзя поменять ответственного за заявку, переданную в бухгалтерию',
    resetChangeConsultantRef,
  );

  return (
    <Form form={form} onFinish={onFinish} layout="vertical" name="userInfo" className={style.userInfo}>
      <div className={style.groupItem}>
        <Form.Item name="tabel" label="Табельный номер" labelAlign="left" className={style.inputWrap}>
          <Input className="userInput" disabled />
        </Form.Item>
        <Form.Item name="division" label="Подразделение" labelAlign="left" className={style.inputWrap}>
          <Input className="userInput" disabled />
        </Form.Item>
      </div>

      <div className={style.groupItem}>
        <Form.Item name="position" label="Должность" labelAlign="left" className={style.inputWrap}>
          <Input className="userInput" disabled />
        </Form.Item>

        {allSubordinates && (
          <Form.Item
            name="consultantId"
            label="Ответственный HR"
            labelAlign="left"
            className={style.inputWrap}
            rules={[
              {
                validator: async (_, v) => {
                  if (v === referencesHrId) return Promise.reject();
                },
                message: 'Выберите нового ответственного',
              },
            ]}
          >
            <Select placeholder="Выберите из списка" onSelect={selectHandler} disabled={selectDisabled}>
              {allSubordinates.map(({ id, name, middleName, lastName }) => (
                <Select.Option key={id} value={id}>
                  {`${lastName} ${name} ${middleName ?? ''}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </div>

      <div className={style.groupItem}>
        <Form.Item name="accountant" label="Ответственный" labelAlign="left" className={style.inputWrap}>
          <Input className="userInput" disabled />
        </Form.Item>

        {dataReferences[0].gettingMethod && dataReferences[0].gettingMethod !== 'Скан на эл. почту' && (
          <Form.Item
            name="exemplarsAmount"
            label="Количество экземпляров"
            labelAlign="left"
            className={style.inputWrap}
          >
            <Input className="userInput" disabled />
          </Form.Item>
        )}
      </div>

      {btnVisible && (
        <Form.Item labelAlign="left" className={style.btn}>
          <Button htmlType="submit" type="primary">
            Сохранить
          </Button>
        </Form.Item>
      )}
    </Form>
  );
};

export default ReferencesUserForm;
